/**
 * The closed-loop, target-tracking scripts: pure pursuit, the lead turn, and
 * the extend (running away). Everything here still returns plain
 * `FlightInputs` — the AI points its nose by rolling and pulling, the way an
 * aeroplane does, not by steering a velocity vector.
 *
 * Two regimes, because one law does not cover both:
 *
 * - **Gross** (target more than `FINE_TRACKING_RAD` off the nose) — bank-to-turn.
 *   Roll until the target sits in the aircraft's pull plane, then pull. For a
 *   target *below* the nose that means rolling most of the way over and pulling
 *   down to it, which is what a WWI scout with feeble elevator authority in
 *   push actually has to do.
 * - **Fine** (inside the cone) — hold the bank and make small direct
 *   corrections in pitch and rudder, so the gunsight settles instead of
 *   snap-rolling every time the target drifts a degree below the nose.
 */

import type { FlightInputs } from "../flight/inputs.ts";
import { bankAngle } from "../flight/math3d.ts";
import { clamp } from "../flight/units.ts";
import {
  coordinateRudder,
  energyLimitedAlpha,
  inputs,
  limitBank,
  pitchToAlpha,
  pitchToAttitude,
  rollToBank,
  sustainableBankRad,
  wrapAngle,
} from "./autopilot.ts";
import { aimPoint, bodyOf, relativeGeometry, type CombatBody } from "./geometry.ts";
import type { ManeuverContext } from "./maneuvers.ts";

/**
 * Half-angle of the fine-tracking cone, radians (20°). Wider than the firing
 * cone on purpose: measured over a hundred scripted duels, handing the last
 * twenty degrees to the settling law rather than to bank-to-turn produced
 * markedly more decided fights, because the gunsight stops hunting.
 */
export const FINE_TRACKING_RAD = 0.35;

/**
 * How far to roll, from where the target sits in the body frame: the angle from
 * the aircraft's "up" to the target's off-axis offset. Positive rolls right.
 */
export function rollDeltaFor(bearingRad: number, elevationRad: number): number {
  return Math.atan2(bearingRad, elevationRad);
}

/**
 * How far below the nose a target can be before the AI stops pushing and starts
 * rolling. Inside this band a shallow dive puts the nose on it; past it, a WWI
 * scout has to roll and pull, because it has almost no negative-g authority.
 */
export const PUSH_LIMIT_RAD = 0.8;

function fineTracking(context: ManeuverContext, bearingRad: number, elevationRad: number): FlightInputs {
  const { sim, tunables } = context;
  const alphaCeiling = energyLimitedAlpha(sim, tunables);
  return inputs({
    pitch: pitchToAlpha(sim, clamp(sim.readout.alphaRad + elevationRad, -alphaCeiling, alphaCeiling), tunables),
    // A gentle bank proportional to the azimuth error: enough to close a few
    // degrees of bearing without turning a tracking correction into a turn.
    roll: rollToBank(sim, limitBank(bearingRad * 3, sustainableBankRad(sim)), tunables),
    yaw: clamp(bearingRad * tunables.aiRudderGain, -0.6, 0.6),
    throttle: 1,
  });
}

/**
 * True when pitch and rudder alone can put the nose on the target. Notably this
 * includes a target well *below* the nose but straight ahead — pushing over
 * into a dive at it is right, and rolling 180° to pull down at it (which is
 * what the bank-to-turn law would do, since the target is on the far side of
 * the wing line) is the mistake that lets a diving bounce fall behind.
 */
function withinFineTracking(bearingRad: number, elevationRad: number): boolean {
  if (Math.abs(bearingRad) >= FINE_TRACKING_RAD) return false;
  return elevationRad > -PUSH_LIMIT_RAD;
}

/**
 * Where the pilot *believes* the solution is: the real bearing and elevation
 * plus their own standing aiming mistake. Everything downstream — the fine/gross
 * split, the roll command, the settling law — reads this rather than the truth,
 * which is exactly what makes a poor shot fly the nose to the wrong place and
 * watch a straight burst go past.
 */
function perceived(context: ManeuverContext, aimBody: CombatBody): { bearingRad: number; elevationRad: number } {
  const view = relativeGeometry(bodyOf(context.sim), aimBody);
  return {
    bearingRad: view.bearingRad + context.aimErrorAzRad,
    elevationRad: view.elevationRad + context.aimErrorElRad,
  };
}

function trackingInputs(context: ManeuverContext, aimBody: CombatBody): FlightInputs {
  const { sim, tunables } = context;
  const view = perceived(context, aimBody);
  const angleOffRad = Math.hypot(view.bearingRad, view.elevationRad);
  if (withinFineTracking(view.bearingRad, view.elevationRad)) {
    return fineTracking(context, view.bearingRad, view.elevationRad);
  }

  // Energy-limited: a bank the wing cannot hold turns the pursuit into the
  // manual's spiral dive, which loses the fight faster than a wide turn does.
  const bankTarget = limitBank(
    bankAngle(sim.orientation) + rollDeltaFor(view.bearingRad, view.elevationRad),
    sustainableBankRad(sim),
  );
  return inputs({
    pitch: pitchToAlpha(sim, energyLimitedAlpha(sim, tunables) * clamp(angleOffRad / 0.6, 0.2, 1), tunables),
    roll: rollToBank(sim, bankTarget, tunables),
    yaw: coordinateRudder(sim, tunables),
    throttle: 1,
  });
}

/** Pure pursuit: nose straight at the target, no lead. What a novice flies. */
export function pursueScript(context: ManeuverContext): FlightInputs {
  const { sim, tunables } = context;
  if (!context.target) return inputs({ pitch: pitchToAttitude(sim, 0, tunables), throttle: 1 });
  return trackingInputs(context, context.target);
}

/**
 * The lead turn: aim at where the target *will* be, weighted by how much of the
 * solution this pilot is good enough to apply (`leadQuality`, from the skill
 * profile). A novice's fractional lead is a visibly late gunsight; an ace's
 * full one is on the money.
 */
export function leadTurnScript(context: ManeuverContext): FlightInputs {
  const { sim, target, tunables } = context;
  if (!target) return pursueScript(context);
  const solution = aimPoint(bodyOf(sim), target, tunables.gunMuzzleSpeedMps);
  const quality = clamp(context.leadQuality, 0, 1);
  const point = {
    x: target.position.x + (solution.point.x - target.position.x) * quality,
    y: target.position.y + (solution.point.y - target.position.y) * quality,
    z: target.position.z + (solution.point.z - target.position.z) * quality,
  };
  return trackingInputs(context, { position: point, velocity: target.velocity, orientation: target.orientation });
}

/**
 * Extend: separate. Turn onto the reciprocal of the bearing to the threat,
 * nose a touch down for speed, full throttle. With no threat to run from it
 * just flies straight, which is the right thing for a patrol leg.
 */
export function extendScript(context: ManeuverContext): FlightInputs {
  const { sim, geometry, tunables } = context;
  if (!geometry) {
    return inputs({
      pitch: pitchToAttitude(sim, 0, tunables),
      roll: rollToBank(sim, 0, tunables),
      yaw: coordinateRudder(sim, tunables),
      throttle: 1,
    });
  }
  const away = wrapAngle(geometry.bearingRad + Math.PI);
  const settled = Math.abs(away) < 0.2;
  return inputs({
    pitch: pitchToAttitude(sim, settled ? -0.12 : 0, tunables),
    roll: rollToBank(sim, settled ? 0 : clamp(away * 1.4, -1.1, 1.1), tunables),
    yaw: coordinateRudder(sim, tunables),
    throttle: 1,
  });
}
